import type { Theme } from '../types/theme';
import type { Viewport } from './ViewportSwitcher';
import { getAccessibleAccentColor, getAccessibleTextColor, withHexAlpha } from '../utils/color';

type PageType = 'landing' | 'dashboard';

interface ThemeMiniPreviewProps {
  theme: Theme;
  pageType: PageType;
  viewport: Viewport;
}

function hashWidth(themeId: string, index: number): number {
  let hash = index * 53 + 17;
  for (const char of themeId) {
    hash = (hash * 29 + char.charCodeAt(0)) % 997;
  }
  return 34 + (hash % 44);
}

function safeSurface(theme: Theme): string {
  return theme.colors.surface || theme.colors.background;
}

function LandingDesktopPreview({ theme }: { theme: Theme }) {
  const text = getAccessibleTextColor(theme.colors.textPrimary, theme.colors.background);
  const muted = getAccessibleTextColor(theme.colors.textMuted, theme.colors.background, 3);
  const ctaText = getAccessibleTextColor(theme.colors.onPrimary, theme.colors.primary);

  return (
    <div className="flex h-full flex-col p-4" style={{ backgroundColor: theme.colors.background }}>
      <div className="mb-5 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <div className="h-4 w-4 rounded" style={{ backgroundColor: theme.colors.primary }} />
          <div className="h-2.5 w-16 rounded-full" style={{ backgroundColor: withHexAlpha(text, 'd9') }} />
        </div>
        <div className="flex gap-1.5">
          {[0, 1, 2].map((item) => (
            <div key={item} className="h-2 w-9 rounded-full" style={{ backgroundColor: withHexAlpha(muted, '66') }} />
          ))}
        </div>
      </div>

      <div className="grid flex-1 grid-cols-[1.15fr_0.85fr] gap-4">
        <div className="min-w-0 pt-1">
          <div className="mb-2 h-3 w-16 rounded-full" style={{ backgroundColor: withHexAlpha(theme.colors.primary, '26') }} />
          <div className="space-y-2">
            <div className="h-3.5 rounded-full" style={{ width: `${hashWidth(theme.id, 1)}%`, backgroundColor: text }} />
            <div className="h-3.5 w-4/5 rounded-full" style={{ backgroundColor: text }} />
            <div className="h-3.5 w-2/3 rounded-full" style={{ backgroundColor: text }} />
          </div>
          <div className="mt-4 flex items-center gap-2">
            <div
              className="h-7 w-20 rounded"
              style={{ backgroundColor: theme.colors.primary, borderRadius: theme.radius.button, color: ctaText }}
            />
            <div
              className="h-7 w-16 rounded border"
              style={{ borderColor: theme.colors.border, borderRadius: theme.radius.button }}
            />
          </div>
        </div>

        <div
          className="grid grid-rows-[1fr_auto] gap-2 border p-2"
          style={{
            backgroundColor: safeSurface(theme),
            borderColor: theme.colors.border,
            borderRadius: theme.radius.panel,
          }}
        >
          <div className="space-y-1.5">
            {[0, 1, 2].map((item) => (
              <div
                key={item}
                className="h-3 rounded-full"
                style={{
                  width: `${hashWidth(theme.id, item + 4)}%`,
                  backgroundColor: item === 0 ? theme.colors.primary : theme.colors.border,
                }}
              />
            ))}
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {[0, 1, 2].map((item) => (
              <div
                key={item}
                className="h-8 border"
                style={{
                  backgroundColor: theme.colors.surfaceElevated || theme.colors.background,
                  borderColor: theme.colors.border,
                  borderRadius: theme.radius.card,
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function DashboardDesktopPreview({ theme }: { theme: Theme }) {
  const text = getAccessibleTextColor(theme.colors.textPrimary, theme.colors.background);
  const muted = getAccessibleTextColor(theme.colors.textMuted, theme.colors.background, 3);

  return (
    <div className="flex h-full" style={{ backgroundColor: theme.colors.background }}>
      <div className="w-16 border-r p-3" style={{ backgroundColor: safeSurface(theme), borderColor: theme.colors.border }}>
        <div className="mb-5 h-5 w-5 rounded" style={{ backgroundColor: theme.colors.primary }} />
        <div className="space-y-2">
          {[0, 1, 2, 3].map((item) => (
            <div
              key={item}
              className="h-2 rounded-full"
              style={{ width: item === 1 ? '85%' : '65%', backgroundColor: item === 0 ? theme.colors.primary : withHexAlpha(muted, '73') }}
            />
          ))}
        </div>
      </div>

      <div className="min-w-0 flex-1 p-4">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <div className="mb-1.5 h-3 w-24 rounded-full" style={{ backgroundColor: text }} />
            <div className="h-2 w-16 rounded-full" style={{ backgroundColor: withHexAlpha(muted, '73') }} />
          </div>
          <div className="h-7 w-16 rounded" style={{ backgroundColor: theme.colors.primary, borderRadius: theme.radius.button }} />
        </div>

        <div className="mb-3 grid grid-cols-3 gap-2">
          {[0, 1, 2].map((item) => (
            <div
              key={item}
              className="h-12 border p-2"
              style={{
                backgroundColor: safeSurface(theme),
                borderColor: theme.colors.border,
                borderRadius: theme.radius.card,
              }}
            >
              <div className="mb-1.5 h-2 w-8 rounded-full" style={{ backgroundColor: withHexAlpha(muted, '73') }} />
              <div className="h-3 rounded-full" style={{ width: `${hashWidth(theme.id, item + 2)}%`, backgroundColor: item === 1 ? theme.colors.primary : text }} />
            </div>
          ))}
        </div>

        <div
          className="h-16 border p-2"
          style={{ backgroundColor: safeSurface(theme), borderColor: theme.colors.border, borderRadius: theme.radius.card }}
        >
          <div className="mb-2 flex items-end gap-1">
            {[0, 1, 2, 3, 4, 5].map((item) => (
              <div
                key={item}
                className="w-full rounded-t"
                style={{
                  height: `${16 + ((item * 9 + theme.id.length * 3) % 30)}px`,
                  backgroundColor: item === 4 ? theme.colors.primary : theme.colors.border,
                }}
              />
            ))}
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[0, 1, 2].map((item) => (
              <div key={item} className="h-1.5 rounded-full" style={{ backgroundColor: withHexAlpha(muted, '4d') }} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function MobileFrame({ theme, children }: { theme: Theme; children: React.ReactNode }) {
  return (
    <div className="flex h-full items-center justify-center p-3" style={{ backgroundColor: safeSurface(theme) }}>
      <div
        className="h-full w-[112px] overflow-hidden border p-2"
        style={{
          backgroundColor: theme.colors.background,
          borderColor: getAccessibleAccentColor(theme.colors.border, safeSurface(theme), theme.colors.primary, 2),
          borderRadius: '18px',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function LandingMobilePreview({ theme }: { theme: Theme }) {
  const text = getAccessibleTextColor(theme.colors.textPrimary, theme.colors.background);
  const muted = getAccessibleTextColor(theme.colors.textMuted, theme.colors.background, 3);

  return (
    <MobileFrame theme={theme}>
      <div className="mb-3 flex items-center justify-between">
        <div className="h-3.5 w-3.5 rounded" style={{ backgroundColor: theme.colors.primary }} />
        <div className="h-1.5 w-9 rounded-full" style={{ backgroundColor: withHexAlpha(muted, '73') }} />
      </div>
      <div className="space-y-1.5">
        <div className="h-2.5 w-16 rounded-full" style={{ backgroundColor: text }} />
        <div className="h-2.5 w-20 rounded-full" style={{ backgroundColor: text }} />
        <div className="h-2.5 w-12 rounded-full" style={{ backgroundColor: text }} />
      </div>
      <div className="my-3 h-6 rounded" style={{ backgroundColor: theme.colors.primary, borderRadius: theme.radius.button }} />
      <div className="space-y-1.5">
        {[0, 1, 2].map((item) => (
          <div
            key={item}
            className="h-8 border p-1.5"
            style={{
              backgroundColor: safeSurface(theme),
              borderColor: theme.colors.border,
              borderRadius: theme.radius.card,
            }}
          >
            <div className="h-1.5 rounded-full" style={{ width: `${hashWidth(theme.id, item + 5)}%`, backgroundColor: item === 1 ? theme.colors.primary : withHexAlpha(muted, '80') }} />
          </div>
        ))}
      </div>
    </MobileFrame>
  );
}

function DashboardMobilePreview({ theme }: { theme: Theme }) {
  const text = getAccessibleTextColor(theme.colors.textPrimary, theme.colors.background);
  const muted = getAccessibleTextColor(theme.colors.textMuted, theme.colors.background, 3);

  return (
    <MobileFrame theme={theme}>
      <div className="mb-3 flex items-center justify-between">
        <div className="h-3 w-10 rounded-full" style={{ backgroundColor: text }} />
        <div className="h-4 w-4 rounded-full" style={{ backgroundColor: theme.colors.primary }} />
      </div>
      <div className="mb-2 grid grid-cols-2 gap-1.5">
        {[0, 1].map((item) => (
          <div
            key={item}
            className="h-9 border p-1.5"
            style={{ backgroundColor: safeSurface(theme), borderColor: theme.colors.border, borderRadius: theme.radius.card }}
          >
            <div className="mb-1 h-1.5 w-7 rounded-full" style={{ backgroundColor: withHexAlpha(muted, '73') }} />
            <div className="h-2 rounded-full" style={{ width: `${hashWidth(theme.id, item + 8)}%`, backgroundColor: item === 0 ? theme.colors.primary : text }} />
          </div>
        ))}
      </div>
      <div className="mb-2 h-10 border p-1.5" style={{ backgroundColor: safeSurface(theme), borderColor: theme.colors.border, borderRadius: theme.radius.card }}>
        <div className="flex h-full items-end gap-1">
          {[0, 1, 2, 3].map((item) => (
            <div
              key={item}
              className="w-full rounded-t"
              style={{
                height: `${8 + ((item * 7 + theme.id.length) % 22)}px`,
                backgroundColor: item === 2 ? theme.colors.primary : theme.colors.border,
              }}
            />
          ))}
        </div>
      </div>
      <div className="space-y-1.5">
        {[0, 1, 2].map((item) => (
          <div key={item} className="flex items-center gap-1.5">
            <div className="h-3 w-3 rounded" style={{ backgroundColor: item === 0 ? theme.colors.primary : theme.colors.border }} />
            <div className="h-1.5 flex-1 rounded-full" style={{ backgroundColor: withHexAlpha(muted, '73') }} />
          </div>
        ))}
      </div>
    </MobileFrame>
  );
}

export function ThemeMiniPreview({ theme, pageType, viewport }: ThemeMiniPreviewProps) {
  const isMobile = viewport === 'mobile';
  const preview = pageType === 'landing'
    ? isMobile ? <LandingMobilePreview theme={theme} /> : <LandingDesktopPreview theme={theme} />
    : isMobile ? <DashboardMobilePreview theme={theme} /> : <DashboardDesktopPreview theme={theme} />;

  return (
    <div className="h-44 overflow-hidden" aria-hidden="true">
      {preview}
    </div>
  );
}
